/**
 * Hook para buscar desvios de ponto recentes
 * 
 * Utiliza React Query para cache e gerenciamento de estado assíncrono.
 * Retorna dados de check-ins atrasados para exibição em tabela.
 * 
 * @module hooks/data/usePontoDesvios
 */

import { useQuery } from '@tanstack/react-query';
import { getPontoDesviosRecentes } from '../../api/api';
import { logHttpError } from '../../utils/errorLogger';
import { getErrorMessage } from '../../utils/errorMessages';

// Query key para cache do React Query
export const PONTO_DESVIOS_QUERY_KEY = ['dashboard', 'ponto-desvios-recentes'];

/**
 * Hook para buscar desvios de ponto recentes (check-ins atrasados)
 * 
 * Configurações do React Query:
 * - staleTime: 5 minutos (dados considerados frescos)
 * - gcTime: 10 minutos (tempo de cache após desmontagem)
 * - retry: 3 tentativas com backoff exponencial
 * 
 * @returns {object} Objeto com dados, estados e funções
 * @property {Array} data - Array de objetos com informações de desvios (registro_ponto_id, agendamento_previsto, checkin_realizado, minutos_atraso, profissional_nome, paciente_nome)
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {string|null} error - Mensagem de erro (null se não houver erro)
 * @property {function} refetch - Função para recarregar os dados manualmente
 * 
 * @example
 * ```javascript
 * const { data, isLoading, error } = usePontoDesvios();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <Table data={data} />;
 * ```
 */
export const usePontoDesvios = () => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: PONTO_DESVIOS_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await getPontoDesviosRecentes();
                // Retorna apenas a array de dados
                return response.data?.data || [];
            } catch (err) {
                logHttpError(err, {
                    component: 'usePontoDesvios',
                    endpoint: '/dashboard/ponto-desvios-recentes',
                });
                throw err;
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados frescos
        gcTime: 1000 * 60 * 10, // 10 minutos - tempo de cache (atualizado de cacheTime para gcTime)
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Manter compatibilidade: retornar error como string (se houver)
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'pontoDesvios',
        })
        : null;

    return {
        data: data || [],
        isLoading,
        isError,
        error: errorMessage,
        refetch,
    };
};
